"use client";

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { toast, useAccountWallet, useModalStore, WalletImage } from "../../../../global-account/react/index.js";
import { cn } from "../../../../shared/utils/cn.js";
import { shortenAddress } from "../../../../shared/utils/formatAddress.js";
import { client } from "../../../../shared/utils/thirdweb.js";
import { ChevronLeft, ChevronRightCircle, Wallet, X, ZapIcon } from "lucide-react";
import { useConnectModal, useDisconnect, useWalletInfo } from "thirdweb/react";
import { createWallet } from "thirdweb/wallets";
import { useConnectedWalletDisplay } from "../../hooks/useConnectedWalletDisplay.js";
export var CryptoPaymentMethodType;
(function (CryptoPaymentMethodType) {
  CryptoPaymentMethodType["NONE"] = "none";
  CryptoPaymentMethodType["CONNECT_WALLET"] = "connect_wallet";
  CryptoPaymentMethodType["GLOBAL_WALLET"] = "global_wallet";
  CryptoPaymentMethodType["TRANSFER_CRYPTO"] = "transfer_crypto";
})(CryptoPaymentMethodType || (CryptoPaymentMethodType = {}));
export const recommendWallets = [createWallet("io.metamask"), createWallet("com.coinbase.wallet"), createWallet("me.rainbow"), createWallet("io.rabby")];
export const connectModalConfig = {
  client,
  setActive: false,
  size: "compact",
  showThirdwebBranding: false,
  wallets: recommendWallets
};
export function CryptoPaymentMethod({
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  isCreatingOrder,
  onBack,
  onSelectPaymentMethod,
  classes
}) {
  const {
    connectedEOAWallet,
    connectedSmartWallet
  } = useAccountWallet();
  const {
    disconnect
  } = useDisconnect();
  const {
    connect: openConnectModal
  } = useConnectModal();
  const {
    data: eoaWalletInfo
  } = useWalletInfo(connectedEOAWallet?.id);
  const globalAddress = connectedSmartWallet?.getAccount()?.address;
  // Use custom hook to determine wallet display logic
  const {
    shouldShowConnectedEOA
  } = useConnectedWalletDisplay(selectedPaymentMethod);
  // Get modal store to block parent modal closing while connect modal is open
  const setClosable = useModalStore(state => state.setClosable);
  // Handle wallet connection using thirdweb modal
  const handleConnectWallet = async () => {
    // Block parent B3 modal from closing while thirdweb connect modal is open
    setClosable(false);
    try {
      // Disconnect current wallet before connecting a new one
      if (connectedEOAWallet) {
        disconnect(connectedEOAWallet);
      }
      const wallet = await openConnectModal(connectModalConfig);
      if (wallet) {
        setSelectedPaymentMethod(CryptoPaymentMethodType.CONNECT_WALLET);
        onSelectPaymentMethod(CryptoPaymentMethodType.CONNECT_WALLET);
        toast.success("Wallet connected");
      }
    } catch (error) {
      console.error("Failed to connect wallet:", error);
      if (error && typeof error === "object" && "message" in error) {
        const errorMessage = error.message.toLowerCase();
        if (errorMessage.includes("rejected") || errorMessage.includes("denied") || errorMessage.includes("cancelled")) {
          // User cancelled - no toast needed
        } else {
          toast.error("Failed to connect wallet");
        }
      }
    } finally {
      // Always re-enable parent modal closing when connect modal closes
      setClosable(true);
    }
  };
  return _jsx("div", {
    className: classes?.container || "crypto-payment-method mx-auto h-fit w-full max-w-[460px] px-5 pb-5 pt-5 sm:px-0 sm:pt-5",
    children: _jsxs("div", {
      className: cn("relative flex flex-col gap-10"),
      children: [_jsx("button", {
        onClick: onBack,
        className: classes?.backButton || "text-as-quaternary hover:text-as-primary absolute flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
        children: _jsx(ChevronLeft, {
          className: "h-6 w-6"
        })
      }), _jsx("div", {
        className: classes?.header || "flex items-center justify-around gap-4",
        children: _jsx("div", {
          className: "flex-1 text-center",
          children: _jsx("h2", {
            className: "text-as-primary text-lg font-semibold",
            children: "Select a payment method"
          })
        })
      }), process.env.NODE_ENV === "development" && _jsxs("div", {
        className: "rounded-lg border border-yellow-500/50 bg-yellow-50 p-3 dark:bg-yellow-950/20",
        children: [_jsx("p", {
          className: "mb-2 text-xs font-semibold text-yellow-800 dark:text-yellow-300",
          children: "\uD83E\uDDEA Toast Test (Dev Only)"
        }), _jsxs("div", {
          className: "flex flex-wrap gap-2",
          children: [_jsx("button", {
            onClick: () => toast.success("Success! Transaction completed"),
            className: "rounded bg-green-600 px-2 py-1 text-xs font-medium text-white hover:bg-green-700",
            children: "Success"
          }), _jsx("button", {
            onClick: () => toast.error("Error! Transaction failed"),
            className: "rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700",
            children: "Error"
          }), _jsx("button", {
            onClick: () => toast.info("Info: Processing your request..."),
            className: "rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700",
            children: "Info"
          }), _jsx("button", {
            onClick: () => toast.warning("Warning: Low balance detected"),
            className: "rounded bg-yellow-600 px-2 py-1 text-xs font-medium text-white hover:bg-yellow-700",
            children: "Warning"
          }), _jsx("button", {
            onClick: () => {
              toast.success("Multiple test 1");
              setTimeout(() => toast.info("Multiple test 2"), 200);
              setTimeout(() => toast.warning("Multiple test 3"), 400);
            },
            className: "rounded bg-purple-600 px-2 py-1 text-xs font-medium text-white hover:bg-purple-700",
            children: "Multiple"
          })]
        })]
      }), _jsxs("div", {
        className: classes?.optionsList || "crypto-payment-methods flex flex-col gap-4",
        children: [(shouldShowConnectedEOA || globalAddress) && _jsxs("div", {
          className: "installed-wallets",
          children: [_jsx("h3", {
            className: "text-as-primary/80 mb-3 text-sm font-medium",
            children: "Connected wallets"
          }), _jsxs("div", {
            className: "space-y-2",
            children: [shouldShowConnectedEOA && _jsx("button", {
              onClick: () => {
                setSelectedPaymentMethod(CryptoPaymentMethodType.CONNECT_WALLET);
                onSelectPaymentMethod(CryptoPaymentMethodType.CONNECT_WALLET);
                toast.success(`Selected ${eoaWalletInfo?.name || "wallet"}`);
              },
              className: cn("crypto-payment-method-connect-wallet eoa-wallet w-full rounded-xl border p-4 text-left transition-all hover:shadow-md", selectedPaymentMethod === CryptoPaymentMethodType.CONNECT_WALLET ? "connected-wallet border-as-brand bg-as-brand/5" : "border-as-border-secondary bg-as-surface-primary hover:border-as-secondary/80"),
              children: _jsxs("div", {
                className: "flex items-center justify-between",
                children: [_jsxs("div", {
                  className: "flex items-center gap-3",
                  children: [_jsx("div", {
                    className: "wallet-icon flex h-10 w-10 items-center justify-center rounded-full bg-blue-100",
                    children: _jsx(Wallet, {
                      className: "h-5 w-5 text-blue-600"
                    })
                  }), _jsxs("div", {
                    className: "flex flex-col",
                    children: [_jsx("span", {
                      className: "text-as-primary font-semibold",
                      children: eoaWalletInfo?.name || "Connected Wallet"
                    }), _jsx("span", {
                      className: "text-as-primary/60 text-sm",
                      children: shortenAddress(connectedEOAWallet?.getAccount()?.address || "")
                    })]
                  })]
                }), _jsxs("div", {
                  className: "flex items-center gap-2",
                  children: [selectedPaymentMethod === CryptoPaymentMethodType.CONNECT_WALLET && _jsx("div", {
                    className: "h-2 w-2 rounded-full bg-green-500"
                  }), _jsx("button", {
                    onClick: e => {
                      e.stopPropagation();
                      if (connectedEOAWallet) {
                        disconnect(connectedEOAWallet);
                      }
                      toast.success("Wallet disconnected");
                      if (selectedPaymentMethod === CryptoPaymentMethodType.CONNECT_WALLET) {
                        setSelectedPaymentMethod(CryptoPaymentMethodType.NONE);
                      }
                    },
                    className: "text-as-primary/60 hover:text-as-primary/80 rounded-lg p-1.5 transition-colors",
                    children: _jsx(X, {
                      className: "h-4 w-4"
                    })
                  })]
                })]
              })
            }), globalAddress && _jsx("button", {
              onClick: () => {
                setSelectedPaymentMethod(CryptoPaymentMethodType.GLOBAL_WALLET);
                onSelectPaymentMethod(CryptoPaymentMethodType.GLOBAL_WALLET);
                toast.success("Selected B3 Account");
              },
              className: cn("crypto-payment-method-global-wallet w-full rounded-xl border p-4 text-left transition-all hover:shadow-md", selectedPaymentMethod === CryptoPaymentMethodType.GLOBAL_WALLET ? "connected-wallet border-as-brand bg-as-brand/5" : "border-as-border-secondary bg-as-surface-primary hover:border-as-secondary/80"),
              children: _jsxs("div", {
                className: "flex items-center justify-between",
                children: [_jsxs("div", {
                  className: "flex items-center gap-3",
                  children: [_jsx(WalletImage, {
                    fallback: _jsx("div", {
                      className: "wallet-icon flex h-10 w-10 items-center justify-center rounded-full bg-purple-100",
                      children: _jsx(Wallet, {
                        className: "h-5 w-5 text-purple-600"
                      })
                    })
                  }), _jsxs("div", {
                    className: "flex flex-col",
                    children: [_jsx("span", {
                      className: "text-as-primary font-semibold",
                      children: "Global Account"
                    }), _jsx("span", {
                      className: "text-as-primary/60 text-sm",
                      children: shortenAddress(globalAddress || "")
                    })]
                  })]
                }), _jsx("div", {
                  className: "flex items-center gap-2",
                  children: selectedPaymentMethod === CryptoPaymentMethodType.GLOBAL_WALLET && _jsx("div", {
                    className: "h-2 w-2 rounded-full bg-green-500"
                  })
                })]
              })
            })]
          })]
        }), _jsxs("div", {
          className: "other-payment-methods",
          children: [_jsx("h3", {
            className: "text-as-primary/80 mb-3 text-sm font-medium",
            children: "Payment methods"
          }), _jsxs("div", {
            className: "space-y-3",
            children: [_jsxs("button", {
              onClick: handleConnectWallet,
              className: "crypto-payment-method-connect-wallet bg-as-surface-primary border-as-border-secondary hover:border-as-secondary/80 group flex w-full items-center justify-between gap-4 rounded-xl border px-4 py-3.5 transition-all duration-200 hover:shadow-md",
              children: [_jsxs("div", {
                className: "flex items-center gap-3",
                children: [_jsx("div", {
                  className: "wallet-icon flex h-8 w-8 items-center justify-center rounded-full bg-blue-100",
                  children: _jsx(Wallet, {
                    className: "h-4 w-4 text-blue-600"
                  })
                }), _jsx("div", {
                  className: "flex flex-col items-start text-left",
                  children: _jsx("h4", {
                    className: "text-as-primary font-semibold",
                    children: "Connect wallet"
                  })
                })]
              }), _jsx(ChevronRightCircle, {
                className: "text-as-primary/40 group-hover:text-as-primary/60 h-5 w-5 transition-colors"
              })]
            }), _jsxs("button", {
              onClick: () => {
                setSelectedPaymentMethod(CryptoPaymentMethodType.TRANSFER_CRYPTO);
                onSelectPaymentMethod(CryptoPaymentMethodType.TRANSFER_CRYPTO);
              },
              disabled: isCreatingOrder,
              className: "crypto-payment-method-transfer bg-as-surface-primary border-as-border-secondary hover:border-as-secondary/80 group flex w-full items-center justify-between gap-4 rounded-xl border px-4 py-3.5 transition-all duration-200 hover:shadow-md",
              children: [_jsxs("div", {
                className: "flex items-center gap-3",
                children: [_jsx("div", {
                  className: "wallet-icon flex h-8 w-8 items-center justify-center rounded-full bg-orange-100",
                  children: _jsx(ZapIcon, {
                    className: "h-4 w-4"
                  })
                }), _jsx("div", {
                  className: "flex flex-col items-start text-left",
                  children: _jsx("h4", {
                    className: "text-as-primary font-semibold",
                    children: "Transfer crypto"
                  })
                })]
              }), _jsx(ChevronRightCircle, {
                className: "text-as-primary/40 group-hover:text-as-primary/60 h-5 w-5 transition-colors"
              })]
            })]
          })]
        })]
      })]
    })
  });
}